import { useLayoutEffect, useRef } from "react";

/**
 * Mirror of `value` kept in a ref that always points at the latest
 * value. Useful inside `useEffect`/`useCallback` bodies that need to
 * read the current value without depending on it — putting an inline
 * callback in a dep array would re-run the effect on every render of
 * the consumer.
 *
 * The classic use is event-listener callbacks set up at mount: the
 * handler closes over the ref, the effect deps don't change, and
 * yet calls to `ref.current(...)` always invoke the latest function.
 *
 * `useLayoutEffect` (rather than plain `useEffect`) updates the ref
 * synchronously after DOM mutations but before paint and before any
 * synchronously-fired listener can observe a stale value. Plain
 * `useEffect` would leave a one-render lag where listeners called
 * during the same commit burst would see the previous value. The app
 * is client-only (no SSR), so the SSR caveat that usually pushes
 * libraries to a `useEffect` fallback doesn't apply here.
 */
export function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}
